// Disabled_Notifications_Modal.jsx
import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Modal, Switch } from "@/config/antd";
import { CloseOutlined } from "@/config/antd";
import Back from "@/components/specific/Back";
import WebIcon from "@/components/common/Web_Icons";
import SuccessAlert from "@/components/common/alerts/Success";
import ErrorAlert from "@/components/common/alerts/Error";
import {
  updateProfileAsync,
  selectNotificationPreferences,
} from "@/store/redux/slices/user/user_slice";


const ALERT_OPTIONS = [
  {
    id: "approvals",
    title: "Funds Review Request",
    description: "When a fund review request is sent",
    icon: "Wallet",
  },
  {
    id: "messages",
    title: "Users Invite Acceptance",
    description: "When someone accepts invite",
    icon: "UserInvite",
  },
  {
    id: "campaigns",
    title: "Events",
    description: "When an event is starting or ending",
    icon: "Calendar2",
  },
  {
    id: "subscription_ending",
    title: "Subscription Ending",
    description: "When subscription is ending",
    icon: "Money",
  },
];

const DisabledNotificationsModal = ({ isVisible, onClose }) => {
  const dispatch = useDispatch();
  const notificationPreferences = useSelector(selectNotificationPreferences);

 
  const activeTypesRef = useRef(new Set());
  const [, forceRender] = useState(0);

  const setActiveTypes = useCallback((newSet) => {
    activeTypesRef.current = newSet;
    forceRender((n) => n + 1);
  }, []);

  const [loadingId, setLoadingId] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  
  useEffect(() => {
    if (isVisible && notificationPreferences?.types) {
      console.log(
        "[NotificationAlerts] Seeding types from Redux:",
        notificationPreferences.types
      );
      setActiveTypes(new Set(notificationPreferences.types));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const handleToggle = async (typeId, checked) => {
    setLoadingId(typeId);

    const currentTypes = Array.from(activeTypesRef.current);
    const updatedTypesArr = checked
      ? [...currentTypes, typeId]
      : currentTypes.filter((t) => t !== typeId);

    // eslint-disable-next-line no-unused-vars
    const { _id: _prefId, ...prefsWithoutId } = notificationPreferences || {};

    const payload = {
      notificationPreferences: {
        ...prefsWithoutId,
        types: updatedTypesArr,
      },
    };

    console.log("[NotificationAlerts] Toggle →", typeId, ":", checked);
    console.log("[NotificationAlerts] Current types:", currentTypes);
    console.log("[NotificationAlerts] Sending payload:", JSON.stringify(payload, null, 2));

    try {
      const result = await dispatch(updateProfileAsync(payload)).unwrap();

      console.log("[NotificationAlerts] Server confirmed save. Result message:", result?.message);

     
      setActiveTypes(new Set(updatedTypesArr));
      setShowSuccess(true);
    } catch (err) {
      console.error("[NotificationAlerts] Update failed:", err);
      setErrorMessage(
        typeof err === "string" ? err : "Failed to update notification preference."
      );
      setShowError(true);
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <>
      <Modal
        open={isVisible}
        onCancel={onClose}
        footer={null}
        centered
        width={500}
        closeIcon={null}
        styles={{
          body: { padding: 0 },
          content: {
            padding: 0,
            borderRadius: "16px",
            overflow: "hidden",
          },
          mask: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
        }}>
        <div className="flex flex-col bg-white">
          {/* Header */}
          <div className="p-4 flex items-center justify-between border-b border-gray-200">
            <div className="flex items-center gap-4">
              <Back removeText={true} color="black" onClick={onClose} />
              <h2 className="text-bold-x text-primary">Notification Alerts</h2>
            </div>
            <CloseOutlined
              onClick={onClose}
              className="cursor-pointer"
              style={{
                color: "var(--veegil-color-primary)",
                backgroundColor: "var(--veegil-color-secondary)",
                borderRadius: "50%",
                padding: "4px",
              }}
            />
          </div>

          {/* Body */}
          <div className="p-6">
            <div className="space-y-4">
              {ALERT_OPTIONS.map((option) => (
                <div
                  key={option.id}
                  className="flex items-center justify-between p-4 bg-white rounded-lg transition-all duration-300">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center">
                      <WebIcon icon={option.icon} className="w-8 h-8" />
                    </div>
                    <div>
                      <h3 className="text-bold text-black">{option.title}</h3>
                      <p className="text-small dark-mode-text">
                        {option.description}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={activeTypesRef.current.has(option.id)}
                    onChange={(checked) => handleToggle(option.id, checked)}
                    loading={loadingId === option.id}
                    disabled={loadingId !== null && loadingId !== option.id}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {/* Success feedback modal */}
      <SuccessAlert
        isVisible={showSuccess}
        onClose={() => setShowSuccess(false)}
        header="Preference Updated"
        paragraph="Your notification alert preference has been saved."
        buttonText="Done"
        onButtonClick={() => setShowSuccess(false)}
      />

      {/* Error feedback modal */}
      <ErrorAlert
        isVisible={showError}
        onClose={() => setShowError(false)}
        header="Update Failed"
        paragraph={errorMessage}
        buttonText="Try Again"
        handleRecovery={() => setShowError(false)}
      />
    </>
  );
};

DisabledNotificationsModal.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
};

export default DisabledNotificationsModal;
